import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, memo } from "react";

export type NovaState =
  | "idle"
  | "listening"
  | "thinking"
  | "explaining"
  | "encouraging"
  | "correct"
  | "incorrect"
  | "streak"
  | "levelUp"
  | "sessionComplete";

export type NovaSize = "sm" | "md" | "lg";

interface NovaOrbProps {
  state?: NovaState;
  stage?: number;
  size?: NovaSize;
  className?: string;
}

const sizeMap = { sm: 40, md: 80, lg: 128 };

const stateColors: Record<NovaState, { core: string; glow: string; accent: string }> = {
  idle:            { core: "#3B52D4", glow: "#4F6AE8", accent: "#6B7FFF" },
  listening:       { core: "#2D4A9E", glow: "#3B5BB8", accent: "#5570CC" },
  thinking:        { core: "#0EA5E9", glow: "#38BDF8", accent: "#7DD3FC" },
  explaining:      { core: "#6366F1", glow: "#818CF8", accent: "#A5B4FC" },
  encouraging:     { core: "#8B5CF6", glow: "#A78BFA", accent: "#C4B5FD" },
  correct:         { core: "#FFFFFF", glow: "#E0E7FF", accent: "#F0F4FF" },
  incorrect:       { core: "#1E3A5F", glow: "#2D5A8E", accent: "#0EA5E9" },
  streak:          { core: "#EAB308", glow: "#FACC15", accent: "#FDE68A" },
  levelUp:         { core: "#7C3AED", glow: "#8B5CF6", accent: "#EAB308" },
  sessionComplete: { core: "#4F6AE8", glow: "#6B7FFF", accent: "#A78BFA" },
};

function getStateAnimation(state: NovaState) {
  switch (state) {
    case "idle":
      return { scale: [1, 1.03, 1], opacity: [0.85, 1, 0.85] };
    case "listening":
      return { scale: [1, 0.96, 1], opacity: [0.9, 1, 0.9] };
    case "thinking":
      return { scale: [1, 1.04, 1.02, 1.05, 1], opacity: [0.9, 1, 0.95, 1, 0.9] };
    case "explaining":
      return { scale: [1, 1.06, 1.02, 1], opacity: [0.9, 1, 0.95, 0.9] };
    case "encouraging":
      return { scale: [1, 1.08, 1], opacity: [0.9, 1, 0.9] };
    case "correct":
      return { scale: [1, 1.15, 1.05, 1], opacity: [1, 1, 1, 0.95] };
    case "incorrect":
      return { scale: [1, 0.92, 1], opacity: [0.7, 0.85, 0.9] };
    case "streak":
      return { scale: [1, 1.12, 1.04, 1.1, 1], opacity: [0.9, 1, 0.95, 1, 0.9] };
    case "levelUp":
      return { scale: [1, 1.2, 1.08, 1.15, 1.05, 1], opacity: [0.8, 1, 0.9, 1, 0.95, 0.9] };
    case "sessionComplete":
      return { scale: [1, 1.02, 1], opacity: [0.9, 1, 0.9] };
  }
}

function getStateTiming(state: NovaState) {
  switch (state) {
    case "idle": return { duration: 4, ease: "easeInOut" as const };
    case "listening": return { duration: 2, ease: "easeInOut" as const };
    case "thinking": return { duration: 1.5, ease: "easeInOut" as const };
    case "explaining": return { duration: 3, ease: "easeInOut" as const };
    case "encouraging": return { duration: 2.5, ease: "easeInOut" as const };
    case "correct": return { duration: 0.8, ease: "easeOut" as const };
    case "incorrect": return { duration: 1.2, ease: "easeInOut" as const };
    case "streak": return { duration: 1.5, ease: "easeInOut" as const };
    case "levelUp": return { duration: 2, ease: "easeInOut" as const };
    case "sessionComplete": return { duration: 5, ease: "easeInOut" as const };
  }
}

function NeuralPaths({ stage, size, colors }: { stage: number; size: number; colors: typeof stateColors.idle }) {
  const pathCount = Math.min(stage + 1, 5);
  const radius = size * 0.3;

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className="absolute inset-0 w-full h-full"
      style={{ filter: "blur(0.5px)" }}
    >
      {Array.from({ length: pathCount }).map((_, i) => {
        const angle1 = (i / pathCount) * Math.PI * 2;
        const angle2 = ((i + 1) / pathCount) * Math.PI * 2;
        const cx = size / 2;
        const cy = size / 2;
        const x1 = cx + Math.cos(angle1) * radius * 0.5;
        const y1 = cy + Math.sin(angle1) * radius * 0.5;
        const x2 = cx + Math.cos(angle2) * radius;
        const y2 = cy + Math.sin(angle2) * radius;
        const cpx = cx + Math.cos((angle1 + angle2) / 2) * radius * 0.8;
        const cpy = cy + Math.sin((angle1 + angle2) / 2) * radius * 0.8;

        return (
          <motion.path
            key={i}
            d={`M ${x1} ${y1} Q ${cpx} ${cpy} ${x2} ${y2}`}
            fill="none"
            stroke={colors.accent}
            strokeWidth={stage >= 3 ? 1.5 : 1}
            strokeLinecap="round"
            opacity={0.4 + stage * 0.1}
            initial={{ pathLength: 0 }}
            animate={{ pathLength: [0, 1, 0.5, 1] }}
            transition={{ duration: 3 + i * 0.5, repeat: Infinity, ease: "easeInOut" }}
          />
        );
      })}
      {stage >= 3 && Array.from({ length: stage - 1 }).map((_, i) => {
        const angle = ((i + 0.5) / (stage - 1)) * Math.PI * 2;
        const x = size / 2 + Math.cos(angle) * radius * 0.7;
        const y = size / 2 + Math.sin(angle) * radius * 0.7;
        return (
          <motion.circle
            key={`node-${i}`}
            cx={x}
            cy={y}
            r={1.5}
            fill={colors.accent}
            opacity={0.6}
            animate={{ opacity: [0.3, 0.8, 0.3], r: [1, 2, 1] }}
            transition={{ duration: 2 + i * 0.3, repeat: Infinity, ease: "easeInOut" }}
          />
        );
      })}
    </svg>
  );
}

function Particles({ stage, size, colors }: { stage: number; size: number; colors: typeof stateColors.idle }) {
  const count = Math.min(2 + stage * 2, 12);

  return (
    <>
      {Array.from({ length: count }).map((_, i) => {
        const angle = (i / count) * Math.PI * 2;
        const dist = size * (0.35 + (i % 3) * 0.05);
        const x = Math.cos(angle) * dist;
        const y = Math.sin(angle) * dist;

        return (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: stage >= 4 ? 3 : 2,
              height: stage >= 4 ? 3 : 2,
              backgroundColor: i % 2 === 0 ? colors.accent : colors.glow,
              left: "50%",
              top: "50%",
            }}
            animate={{
              x: [x * 0.8, x, x * 0.9, x * 0.8],
              y: [y * 0.8, y, y * 0.9, y * 0.8],
              opacity: [0.2, 0.6, 0.3, 0.2],
            }}
            transition={{
              duration: 4 + i * 0.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.3,
            }}
          />
        );
      })}
    </>
  );
}

function NovaOrbInner({ state = "idle", stage = 1, size = "md", className = "" }: NovaOrbProps) {
  const px = sizeMap[size];
  const colors = stateColors[state];
  const anim = getStateAnimation(state);
  const timing = getStateTiming(state);
  const clampedStage = Math.max(1, Math.min(5, stage));

  const [prefersReduced, setPrefersReduced] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e: MediaQueryListEvent) => setPrefersReduced(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  const glowSize = px * (0.6 + clampedStage * 0.08);
  const showFlare = state === "correct" || state === "streak" || state === "levelUp";

  return (
    <div
      className={`relative flex items-center justify-center ${className}`}
      style={{ width: px, height: px }}
      role="img"
      aria-label={`Nova AI companion, ${state === "idle" ? "ready" : state === "thinking" ? "thinking" : state === "explaining" ? "explaining" : state === "correct" ? "celebrating correct answer" : state === "incorrect" ? "encouraging after incorrect answer" : state === "streak" ? "celebrating streak" : state === "levelUp" ? "level up celebration" : state === "sessionComplete" ? "session complete" : state}`}
      data-testid="nova-orb"
      data-nova-state={state}
      data-nova-stage={clampedStage}
    >
      <motion.div
        className="absolute rounded-full"
        style={{
          width: glowSize * 1.8,
          height: glowSize * 1.8,
          background: `radial-gradient(circle, ${colors.glow}30 0%, transparent 70%)`,
        }}
        animate={prefersReduced ? {} : {
          scale: anim.scale,
          opacity: anim.opacity.map(o => o * 0.4),
        }}
        transition={{ ...timing, repeat: Infinity }}
      />

      <motion.div
        className="absolute rounded-full"
        style={{
          width: glowSize * 1.2,
          height: glowSize * 1.2,
          background: `radial-gradient(circle, ${colors.glow}50 0%, transparent 70%)`,
        }}
        animate={prefersReduced ? {} : {
          scale: anim.scale,
          opacity: anim.opacity.map(o => o * 0.6),
        }}
        transition={{ ...timing, repeat: Infinity, delay: 0.1 }}
      />

      <motion.div
        className="relative rounded-full overflow-hidden"
        style={{
          width: px * 0.6,
          height: px * 0.6,
          background: `radial-gradient(circle at 35% 35%, ${colors.accent}CC, ${colors.core}FF, ${colors.core}DD)`,
          boxShadow: `0 0 ${px * 0.3}px ${colors.glow}60, inset 0 0 ${px * 0.15}px ${colors.accent}40`,
        }}
        animate={prefersReduced ? {} : {
          scale: anim.scale,
        }}
        transition={{ ...timing, repeat: Infinity }}
      >
        {!prefersReduced && size !== "sm" && (
          <NeuralPaths stage={clampedStage} size={px * 0.6} colors={colors} />
        )}

        <div
          className="absolute rounded-full"
          style={{
            width: "30%",
            height: "30%",
            top: "20%",
            left: "25%",
            background: `radial-gradient(circle, ${colors.accent}90, transparent)`,
            filter: "blur(4px)",
          }}
        />
      </motion.div>

      {!prefersReduced && size !== "sm" && (
        <Particles stage={clampedStage} size={px * 0.5} colors={colors} />
      )}

      <AnimatePresence>
        {showFlare && !prefersReduced && (
          <motion.div
            className="absolute rounded-full"
            style={{
              width: px * 0.7,
              height: px * 0.7,
              background: `radial-gradient(circle, ${colors.accent}80, transparent 70%)`,
            }}
            initial={{ scale: 0.5, opacity: 0.8 }}
            animate={{ scale: 2.5, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: state === "levelUp" ? 1.5 : 0.8, ease: "easeOut" }}
          />
        )}
      </AnimatePresence>

      {clampedStage >= 5 && !prefersReduced && (
        <motion.div
          className="absolute rounded-full"
          style={{
            width: px * 0.65,
            height: px * 0.65,
            border: `1px solid ${colors.accent}30`,
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
      )}
    </div>
  );
}

const NovaOrb = memo(NovaOrbInner);
export default NovaOrb;
